import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import TenantFilter from '../components/TenantFilter';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Plus,
  CalendarIcon,
  Loader2,
  MapPin
} from 'lucide-react';

const statusConfig = {
  criada: { label: 'Criada', color: 'bg-blue-100 text-blue-700' },
  aguardando_inicio: { label: 'Aguardando Início', color: 'bg-yellow-100 text-yellow-700' },
  em_andamento: { label: 'Em Andamento', color: 'bg-orange-100 text-orange-700' },
  finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  aguardando_validacao: { label: 'Aguardando Validação', color: 'bg-purple-100 text-purple-700' },
  contestada: { label: 'Contestada', color: 'bg-amber-100 text-amber-700' },
  aprovado_faturamento: { label: 'Aprovado p/ Faturamento', color: 'bg-emerald-100 text-emerald-700' },
  faturado: { label: 'Faturado', color: 'bg-slate-100 text-slate-700' },
};

const MissionsPage = () => {
  const { token, user } = useAuth();
  const { selectedTenant } = useTenant();
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchMissions();
  }, [token, selectedTenant]);

  const fetchMissions = async () => {
    try {
      let url = '/missions';
      if (user?.role === 'mestre' && selectedTenant && selectedTenant !== 'all') {
        url += `?tenant_cnpj=${selectedTenant}`;
      }
      const data = await api.get(url, token);
      setMissions(data);
    } catch (error) {
      toast.error('Erro ao carregar missões');
    } finally {
      setLoading(false);
    }
  };

  const filteredMissions = missions.filter(m => {
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    const matchesSearch = searchTerm === '' || 
      m.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.reason_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesDate = true;
    if (startDate) {
      matchesDate = matchesDate && new Date(m.created_at) >= startDate;
    }
    if (endDate) {
      matchesDate = matchesDate && new Date(m.created_at) <= endDate;
    }
    
    return matchesStatus && matchesSearch && matchesDate;
  });

  const exportToExcel = () => {
    const csv = [
      ['Código', 'Motivo', 'Cliente', 'Fornecedor', 'Status', 'Data Criação', 'Placa', 'Motorista'].join(','),
      ...filteredMissions.map(m => [
        m.code,
        m.reason_description,
        m.customer_name || '',
        m.supplier_name || '',
        statusConfig[m.status]?.label || m.status,
        new Date(m.created_at).toLocaleDateString('pt-BR'),
        m.vehicle_plate,
        m.driver_name
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `missoes_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Arquivo exportado com sucesso');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="missions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Missões
          </h1>
          <p className="text-slate-500 mt-1">{filteredMissions.length} missões encontradas</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportToExcel} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
          <Button 
            onClick={() => navigate('/missions/new')}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-mission-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Missão
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Tenant Filter */}
            <TenantFilter />
            
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por código, cliente, fornecedor, placa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-40" data-testid="start-date-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {startDate ? format(startDate, 'dd/MM/yyyy') : 'Data Início'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-40" data-testid="end-date-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {endDate ? format(endDate, 'dd/MM/yyyy') : 'Data Fim'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>

            {(searchTerm || statusFilter !== 'all' || startDate || endDate) && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setStartDate(null);
                  setEndDate(null);
                }}
                data-testid="clear-filters-btn"
              >
                Limpar Filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Motivo</th>
                  <th>Local</th>
                  <th>Cliente</th>
                  <th>Fornecedor</th>
                  <th>Placa</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Km Total</th>
                  <th>Tempo</th>
                  <th>SLA</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredMissions.map((mission) => {
                  const config = statusConfig[mission.status];
                  const hasArrived = mission.supplier_arrived_at;
                  const estimatedMinutes = mission.estimated_arrival_minutes || 30;
                  
                  let slaStatus = 'N/A';
                  if (hasArrived && mission.supplier_accepted_at) {
                    const accepted = new Date(mission.supplier_accepted_at);
                    const arrived = new Date(mission.supplier_arrived_at);
                    const actualMinutes = Math.round((arrived - accepted) / 60000);
                    slaStatus = actualMinutes <= estimatedMinutes ? 'OK' : `+${actualMinutes - estimatedMinutes}min`;
                  }
                  
                  // Formatar tempo total
                  const totalMinutes = mission.total_time_minutes || 0;
                  const hours = Math.floor(totalMinutes / 60);
                  const mins = totalMinutes % 60;
                  const timeDisplay = totalMinutes > 0 
                    ? `${hours}h${mins.toString().padStart(2, '0')}` 
                    : '-';
                  
                  return (
                    <tr key={mission.id} data-testid={`mission-row-${mission.id}`}>
                      <td>
                        <span className="font-mono text-sm font-medium text-slate-900">{mission.code}</span>
                      </td>
                      <td className="text-slate-600 max-w-[150px] truncate">{mission.reason_description}</td>
                      <td className="text-slate-600 max-w-[150px] truncate">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {mission.location_address}
                        </div>
                      </td>
                      <td className="text-slate-600">{mission.customer_name || '-'}</td>
                      <td className="text-slate-600">{mission.supplier_name || '-'}</td>
                      <td>
                        <span className="font-mono text-sm">{mission.vehicle_plate}</span>
                      </td>
                      <td>
                        <Badge className={config?.color || 'bg-slate-100'}>
                          {config?.label || mission.status}
                        </Badge>
                      </td>
                      <td className="text-slate-500 text-sm font-mono">
                        {new Date(mission.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="text-center">
                        <span className="font-mono text-sm text-slate-700">
                          {mission.total_km ? `${mission.total_km.toFixed(1)} km` : '-'}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="font-mono text-sm text-slate-700">
                          {timeDisplay}
                        </span>
                      </td>
                      <td>
                        <span className={`text-sm font-medium ${slaStatus === 'OK' ? 'text-green-600' : slaStatus === 'N/A' ? 'text-slate-400' : 'text-red-600'}`}>
                          {slaStatus}
                        </span>
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/missions/${mission.id}`)}
                          data-testid={`view-mission-${mission.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default MissionsPage;
